import { useCallback, useSyncExternalStore } from "react";

/**
 * Vrai si le navigateur se croit en ligne.
 *
 * ## Ce qu'on perd par rapport à NetInfo
 * NetInfo distinguait `isConnected` (rattaché à un réseau) de
 * `isInternetReachable` (Internet réellement joignable), en effectuant un vrai
 * test de joignabilité — ce qui permettait de détecter un routeur sans Internet
 * ou un portail captif.
 *
 * `navigator.onLine` n'offre PAS cette distinction : il ne dit que « une
 * interface réseau est active ». Derrière un portail captif, il vaut `true`
 * alors que rien ne passe.
 *
 * On compense côté requêtes plutôt que côté capteur : les échecs réseau
 * d'Axios restent la source de vérité pour l'UI d'erreur (cf. shared/api).
 * Ce hook ne sert qu'au bandeau hors-ligne, où un faux positif est bénin.
 *
 * Le remplacement fidèle serait un ping périodique du Gateway — coûteux en
 * batterie et en requêtes pour un bandeau. À rouvrir si le support terrain
 * remonte des cas de portail captif.
 */
export function useNetworkStatus(): boolean {
  const subscribe = useCallback((onChange: () => void) => {
    window.addEventListener("online", onChange);
    window.addEventListener("offline", onChange);
    return () => {
      window.removeEventListener("online", onChange);
      window.removeEventListener("offline", onChange);
    };
  }, []);

  const getSnapshot = useCallback(() => navigator.onLine, []);

  // En l'absence de navigator (test, SSR), on se déclare en ligne : afficher un
  // bandeau hors-ligne à tort est plus gênant que l'inverse.
  const getServerSnapshot = useCallback(() => true, []);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
